import { useEffect, useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';
import {
  categorizeDocument,
  isLLMProcessingEnabledForType,
} from 'function-utils';
import useAppConfig from './useAppConfig';

export const FILE_TYPES_AND_EXTENSIONS = {
  'application/pdf': '.pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    '.docx',
  'application/msword': '.doc',
  'application/vnd.ms-powerpoint': '.ppt',
  'application/vnd.ms-excel.sheet.macroEnabled.12': '.xlsm',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.ms-excel': '.xls',
  'text/plain': '.txt',
  'video/mp4': '.mp4',
  'audio/mp4': '.m4a',
  'audio/mpeg': '.mp3',
};

const useDocumentUploader = ({
  initialFiles = [],
  onFilesChange,
  setAllUploadsCompleted,
  setUploadError,
  defaultDocumentType,
  category,
}) => {
  const { userData } = useSelector(({ login }) => login);
  const [isUploading, setIsUploading] = useState(false);
  const [counter, setCounter] = useState(0);
  const [listFiles, setListFiles] = useState(initialFiles);

  const {
    isLoading: isConfigLoading,
    error: configError,
    data: configData,
  } = useAppConfig();

  const cleanPath = (path) => {
    if (!!path) {
      let noFirstSlash = path.startsWith('/') ? path.slice(1) : path;
      let noFileName = noFirstSlash.substring(0, noFirstSlash.lastIndexOf('/'));
      return noFileName;
    } else return '';
  };

  const convertBlob = useCallback(
    (data) => {
      let newFiles = [];
      data.forEach((element) => {
        if (FILE_TYPES_AND_EXTENSIONS[element.type]) {
          let reader = new FileReader();
          reader.readAsDataURL(element);
          reader.onloadend = function () {
            let obj = {};
            obj.document_name = element.name;
            obj.document_size = element.size;
            obj.document_path = reader.result;
            const sendDocumentType =
              defaultDocumentType ||
              categorizeDocument({
                docConfig: configData,
                docString: obj.document_name,
                category,
              });
            obj.document_type = sendDocumentType;
            obj.added_by_user = userData.user_id;
            obj.llmprocessing = isLLMProcessingEnabledForType({
              docConfig: configData,
              newType: sendDocumentType,
              category,
            });
            obj.file_path = !!element.path
              ? cleanPath(element.path)
              : element.webkitRelativePath
                ? cleanPath(element.webkitRelativePath)
                : '';
            newFiles.push(obj);

            if (
              newFiles.length ===
              data.filter((file) => FILE_TYPES_AND_EXTENSIONS[file.type]).length
            ) {
              // Update internal state
              setListFiles((prev) => {
                const combined = [...prev, ...newFiles];
                // Trigger callback if provided
                if (onFilesChange) {
                  onFilesChange(combined);
                }
                return combined;
              });
            }
          };
        }
      });
    },
    [userData, onFilesChange]
  );

  //Function to change the document type of a file
  const setDocumentType = ({ index, newType }) => {
    let llmprocessing = isLLMProcessingEnabledForType({
      docConfig: configData,
      newType: newType,
      category,
    });
    const updatedBasefile = listFiles.map((item, i) => {
      if (i === index) {
        return { ...item, document_type: newType, llmprocessing };
      }
      return item;
    });
    setListFiles(updatedBasefile);
  };

  const startUploading = () => {
    setCounter(0);
    setIsUploading(true);
  };

  const onDrop = useCallback(
    (acceptedFiles, event) => {
      startUploading();
      let files = null;
      if (event?.target?.files) {
        files = Array.from(event?.target?.files);
      } else {
        files = acceptedFiles;
      }
      if (files?.length) {
        convertBlob(files);
        if (event?.target) {
          event.target.value = '';
        }
      }
    },
    [convertBlob]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
    noClick: true,
  });

  // Handle file removal
  const handleRemoveFile = useCallback(
    (index) => {
      setListFiles((prev) => {
        const updated = [...prev];
        updated.splice(index, 1);
        if (onFilesChange) {
          onFilesChange(updated);
        }
        return updated;
      });
    },
    [onFilesChange]
  );

  // Handle adding files programmatically
  const handleAddFiles = useCallback(
    (newFiles) => {
      setListFiles((prev) => {
        const combined = [...prev, ...newFiles];
        if (onFilesChange) {
          onFilesChange(combined);
        }
        return combined;
      });
    },
    [onFilesChange]
  );

  // Update internal state when initialFiles changes
  useEffect(() => {
    if (initialFiles && initialFiles.length > 0) {
      setListFiles(initialFiles);
    }
  }, [initialFiles]);

  useEffect(() => {
    let interval;
    if (isUploading) {
      interval = setInterval(() => {
        setCounter((prevCounter) => {
          if (prevCounter < 100) {
            return prevCounter + 1;
          } else {
            clearInterval(interval);
            setIsUploading(false);
            return prevCounter;
          }
        });
      }, 10);
    }
    return () => clearInterval(interval);
  }, [isUploading]);

  useEffect(() => {
    if (setAllUploadsCompleted) {
      if (isUploading === false) {
        setUploadError?.();
      }
      setAllUploadsCompleted(!isUploading);
    }
  }, [isUploading, setAllUploadsCompleted, setUploadError]);

  return {
    getRootProps,
    getInputProps,
    isUploading,
    counter,
    onDrop,
    listFiles,
    setListFiles,
    handleRemoveFile,
    handleAddFiles,
    setDocumentType,
  };
};

export default useDocumentUploader;
